/* global google */
import React, { useEffect, useState, useCallback } from 'react';
import { useContainer } from 'unstated-next';
import ReactGA from 'react-ga4';
import { useSearchParams } from 'react-router-dom';
import { useCookies } from 'react-cookie';
import { usePostHog } from 'posthog-js/react';
import { Breakpoint, useCurrentBreakpointName } from 'react-socks';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClose, faMap } from '@fortawesome/free-solid-svg-icons';
import Map from './components/Map';
import FindParkingHeader from './components/FindParkingHeader';
import RentParkingModal from './components/RentParkingModal';
import RentParkingSlideoutModal from './components/RentParkingSlideoutModal';
import ShareParkingModal from './components/ShareParkingModal';
import ParkingAreaItem from './components/ParkingAreaItem';
import { GlobalStateContainer } from '../../provider/GlobalStateContainer';
import i18n from '../../i18n/i18n';
import { BreakpointsSize, DefaultLocation } from '../../utils/constants';
import { getNearbyParkingAreas } from '../../services/apiService';

const FindParking = () => {
  const [searchParams] = useSearchParams();
  // eslint-disable-next-line no-unused-vars
  const [_, setCookie] = useCookies([]);
  const posthog = usePostHog();
  const [parkingAreas, setParkingAreas] = useState([]);
  const [calculatedDistance, setCalculatedDistance] = useState(false);
  const [parkingAreaDistances, setParkingAreaDistances] = useState([]);
  const [hoveringParkingAreaId, setHoveringParkingAreaId] = useState();
  const [selectedParkingAreaId, setSelectedParkingAreaId] = useState();
  const [showShareParkingModal, setShowShareParkingModal] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const { searchLocationRangeInKm } = useContainer(GlobalStateContainer);
  const [coordinates, setCoordinates] = useState(null);
  const breakpoint = useCurrentBreakpointName();
  const smallAndDown = [BreakpointsSize.XSMALL, BreakpointsSize.SMALL].includes(
    breakpoint
  );
  const [defaultParkingAreas, setDefaultParkingAreas] = useState([]);

  const calculateDistance = useCallback((parkingAreas) => {
    let service = new google.maps.DistanceMatrixService();
    let destinations = parkingAreas.filter(p => p.active === true).map(p => ({ lat: p.location.latitude, lng: p.location.longitude })).slice(0, 5);
    service.getDistanceMatrix(
      {
        origins: [coordinates ?? DefaultLocation],
        destinations: destinations,
        travelMode: google.maps.TravelMode.WALKING,
      },
      (response, status) => {
        if (status !== "OK") {
          return;
        } else {
          const results = response.rows[0].elements;
          setParkingAreaDistances(results);
          return;
        }
      }
    );
  }, [coordinates])

  const applyFilters = (parkingAreas) => {
    if (!localStorage.parkingFilter) {
      return parkingAreas;
    }

    const filterSettings = JSON.parse(localStorage.parkingFilter);
    if (!filterSettings || Object.keys(filterSettings).length === 0) {
      return parkingAreas;
    }

    return parkingAreas.filter((area) => {
      // Check property type filters
      if (filterSettings.filterLanded && !area.additionalInformation.hasLanded)
        return false;
      if (
        filterSettings.filterApartment &&
        !area.additionalInformation.hasApartment
      )
        return false;
      if (
        filterSettings.filterCommercial &&
        !area.additionalInformation.hasCommercial
      )
        return false;

      // Check other filters
      if (
        filterSettings.filterEvCharging &&
        !area.additionalInformation.hasEvCharging
      )
        return false;
      if (filterSettings.filterHot && !area.additionalInformation.isHot)
        return false;
      if (
        filterSettings.filterReserved &&
        !area.additionalInformation.hasReserved
      )
        return false;

      // Check price range
      if (
        filterSettings.minPrice &&
        area?.displayMonthlyPrice?.min < filterSettings.minPrice * 100
      )
        return false;
      if (
        filterSettings.maxPrice &&
        area?.displayMonthlyPrice?.max > filterSettings.maxPrice * 100
      )
        return false;

      return true;
    });
  };

  const handleChangeFilter = useCallback(() => {
    setParkingAreas(applyFilters(defaultParkingAreas));
  }, [defaultParkingAreas]);

  const handleGetParkingAreas = useCallback(
    async (coordinates) => {
      const response = await getNearbyParkingAreas(
        coordinates,
        searchLocationRangeInKm
      );
      setDefaultParkingAreas(response.data);
      setParkingAreas(applyFilters(response.data));
    },
    [searchLocationRangeInKm]
  );

  const onHoverPin = (id) => {
    setHoveringParkingAreaId(id);
  };

  const onClickPin = (place) => {
    setSelectedParkingAreaId(place.id);

    ReactGA.event({
      category: "Renting",
      action: "User click location pin",
      label: place.address,
    });

    posthog?.capture("click_location_pin", { place: place.address });
  };

  useEffect(() => {
    const affiliateParam = searchParams.get("affiliate");
    const parkingAreaParam = searchParams.get("parkingArea");

    // cookie expires 1 week from now
    const expires = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);

    if (affiliateParam) {
      if (parkingAreaParam) {
        setCookie(parkingAreaParam, affiliateParam, { expires });
      } else {
        setCookie("generalParkingAffiliate", affiliateParam, { expires });
      }
    }

    const lat = searchParams.get("lat");
    const lng = searchParams.get("lng");

    if (lat && lng) {
      const location = { lat: parseFloat(lat), lng: parseFloat(lng) };
      setCoordinates(location);
      handleGetParkingAreas(location);
    }
  }, [searchParams, setCookie, handleGetParkingAreas]);

  useEffect(() => {
    if (!calculatedDistance && parkingAreas.length > 0) {
      setCalculatedDistance(true);
      calculateDistance(parkingAreas);
    }
  }, [calculatedDistance, parkingAreas, calculateDistance]);

  return (
    <>
      <div className="pt-[75px] grid grid-cols-1 md:grid-cols-[40%,60%] md:pl-5">
        {(!smallAndDown || !showMap) && (
          <div className="overflow-x-hidden h-[85vh] shadow-md w-full rounded-lg">
            <FindParkingHeader onChangeFilter={handleChangeFilter} />
            <div className="flex flex-col overflow-y-auto">
              {parkingAreas.map((parkingArea, i) => (
                <ParkingAreaItem
                  key={parkingArea.id}
                  parkingArea={parkingArea}
                  distance={parkingAreaDistances[i]}
                  hoveringParkingAreaId={hoveringParkingAreaId}
                  onClick={() => setSelectedParkingAreaId(parkingArea.id)}
                  onHover={() => setHoveringParkingAreaId(parkingArea.id)}
                />
              ))}
            </div>
            <Breakpoint small down>
              <div className="flex items-center justify-center">
                <button
                  type="button"
                  className="fixed bottom-0 mb-5 text-white text-[12px] font-medium rounded-[30px] flex gap-2 py-3 px-4 items-center bg-primary900"
                  onClick={() => setShowMap(true)}
                >
                  <FontAwesomeIcon icon={faMap} className="w-[15px]" />
                  {i18n.t("findParking.viewMap")}
                </button>
              </div>
            </Breakpoint>
          </div>
        )}
        {(!smallAndDown || showMap) && (
          <div className="w-full md:w-[95%] mx-auto overflow-auto rounded-lg">
            {selectedParkingAreaId && (
              <Breakpoint medium up>
                <RentParkingSlideoutModal
                  parkingArea={parkingAreas.find(
                    (parkingArea) => parkingArea.id === selectedParkingAreaId
                  )}
                  distance={
                    parkingAreaDistances[
                      parkingAreas.findIndex(
                        (parkingArea) =>
                          parkingArea.id === selectedParkingAreaId
                      )
                    ]
                  }
                  onCloseModal={() => setSelectedParkingAreaId(null)}
                  onShowShareModal={() => setShowShareParkingModal(true)}
                />
              </Breakpoint>
            )}
            <Map
              loadingElement={<div className="h-full" />}
              containerElement={<div className="h-[85vh] relative" />}
              mapElement={<div className="h-full rounded-lg shadow-md" />}
              parkingAreas={parkingAreas}
              userLocation={coordinates}
              hoveringParkingAreaId={hoveringParkingAreaId}
              selectedParkingAreaId={selectedParkingAreaId}
              defaultLocation={DefaultLocation}
              onHoverPin={onHoverPin}
              onClickPin={onClickPin}
            />
            <Breakpoint small down>
              <div className="flex items-center justify-center">
                <button
                  type="button"
                  className="fixed bottom-0 mb-5 text-[12px] text-white font-medium rounded-[30px] flex gap-2 py-3 px-4 items-center bg-gray-600 opacity-80"
                  onClick={() => setShowMap(false)}
                >
                  <FontAwesomeIcon icon={faClose} className="w-[15px]" />
                  {i18n.t("findParking.closeMap")}
                </button>
              </div>
            </Breakpoint>
          </div>
        )}
        <Breakpoint small down>
          <RentParkingModal
            isModalOpen={selectedParkingAreaId}
            parkingArea={parkingAreas.find(
              (parkingArea) => parkingArea.id === selectedParkingAreaId
            )}
            distance={
              parkingAreaDistances[
                parkingAreas.findIndex(
                  (parkingArea) => parkingArea.id === selectedParkingAreaId
                )
              ]
            }
            onCloseModal={() => setSelectedParkingAreaId(null)}
            onShowShareModal={() => setShowShareParkingModal(true)}
          />
        </Breakpoint>
      </div>

      {showShareParkingModal && (
        <ShareParkingModal
          parkingArea={parkingAreas.find(
            (parkingArea) => parkingArea.id === selectedParkingAreaId
          )}
          onCloseModal={() => setShowShareParkingModal(false)}
        />
      )}
    </>
  );
};

export default FindParking;
